import { Controller } from '@hotwired/stimulus'
import { RECIPE_CAPTURE_COPY } from '../recipe_capture_copy'

export default class extends Controller {
  static targets = ["panel", "url", "text", "submitButton"]
  static values = {
    createUrl: String,
    errorDestination: { type: String, default: "Saved Recipes" },
    mode: { type: String, default: "url" }
  }

  connect() {
    this.initialPanelHtml = this.panelTarget.innerHTML
  }

  disconnect() {
    this.abortController?.abort()
  }

  async submit(event) {
    event.preventDefault()
    await this.createCapture(this.inputValue())
  }

  async retry(event) {
    event.preventDefault()
    const url = event.params.url?.trim()
    if (!url) return

    await this.createCapture(url, { retryUrl: true })
  }

  async createCapture(input, { retryUrl = false } = {}) {
    this.dispatchCaptureEvent("started")
    this.renderLoading()

    try {
      const body = this.textMode() ? {
        "recipe_capture[input_kind]": "pasted_text",
        "recipe_capture[text]": input
      } : {
        "recipe_capture[input_kind]": "url",
        "recipe_capture[url]": input
      }
      if (retryUrl) body["recipe_capture[retry_url]"] = "1"

      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Accept": "text/html",
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          "X-CSRF-Token": this.csrfToken()
        },
        body: new URLSearchParams(body),
        signal: this.requestSignal()
      })
      const html = await response.text()

      if (response.ok || response.status === 422) {
        this.panelTarget.innerHTML = html
        if (this.panelTarget.querySelector("[data-saved-recipe-capture-auto-edit='true']")) {
          const editLink = this.panelTarget.querySelector("[data-saved-recipe-capture-edit-link='true']")
          if (editLink) {
            this.dispatchCaptureEvent("completed")
            editLink.click()
            return
          }
        }
        this.dispatchCaptureEvent(response.ok ? "completed" : "failed")
        this.focusResult()
        return
      }

      this.renderRequestError()
      this.dispatchCaptureEvent("failed")
    } catch (error) {
      if (error?.name === "AbortError") return

      this.renderRequestError()
      this.dispatchCaptureEvent("failed")
    }
  }

  reset() {
    this.panelTarget.innerHTML = this.initialPanelHtml
    this.dispatchCaptureEvent("reset")
    if (this.textMode() && this.hasTextTarget) {
      this.textTarget.focus()
    } else if (this.hasUrlTarget) {
      this.urlTarget.focus()
    }
  }

  renderLoading() {
    const copy = this.textMode() ? RECIPE_CAPTURE_COPY.text : RECIPE_CAPTURE_COPY.url
    this.panelTarget.innerHTML = `
      <div class="space-y-3" role="status" aria-live="polite">
        <h3 id="${this.headingId()}" class="text-lg font-semibold tracking-normal" tabindex="-1">${copy.loadingHeading}</h3>
        <p class="ec-field-hint mt-1">${copy.loadingHelper}</p>
      </div>
    `
  }

  renderRequestError() {
    const copy = this.textMode() ? RECIPE_CAPTURE_COPY.text : RECIPE_CAPTURE_COPY.url
    const helper = `Try again, or return to ${this.errorDestinationValue} and add the details yourself.`
    this.panelTarget.innerHTML = `
      <div class="space-y-3" role="alert" aria-live="assertive">
        <h3 id="${this.headingId()}" class="text-lg font-semibold tracking-normal" tabindex="-1">${copy.requestErrorHeading}</h3>
        <p class="ec-field-hint mt-1">${helper}</p>
        <button type="button" class="ec-object-action" data-action="saved-recipe-capture#reset">${copy.retryLabel}</button>
      </div>
    `
  }

  textMode() {
    return this.modeValue === "text"
  }

  inputValue() {
    if (this.textMode()) return this.hasTextTarget ? this.textTarget.value : ""

    return this.hasUrlTarget ? this.urlTarget.value : ""
  }

  focusResult() {
    const result = this.panelTarget.querySelector("h2, h3")
    if (result) {
      result.setAttribute("tabindex", "-1")
      result.focus()
      return
    }

    this.panelTarget.querySelector("[aria-invalid='true']")?.focus()
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  requestSignal() {
    this.abortController?.abort()
    this.abortController = new AbortController()
    return this.abortController.signal
  }

  headingId() {
    return this.element.getAttribute("aria-labelledby") || "saved-recipe-capture-heading"
  }

  dispatchCaptureEvent(name) {
    this.element.dispatchEvent(new CustomEvent(`saved-recipe-capture:${name}`, { bubbles: true }))
  }
}
